import { useI18n } from 'vue-i18n'
import type { PromptGroup, PromptItem } from '../types'

type PromptLanguage = 'en' | 'de'

/* prompt texts by language and prompt id; both languages carry the same ids */
const PROMPT_TEXTS: Record<PromptLanguage, Record<string, string>> = {
  en: {
    '1': 'Can I see the list of my branches?',
    '2': 'How did each branch perform in revenue last month?',
    '3': 'What was total revenue this year?',
    '4': 'What was the average revenue this year?',
    '5': 'What was the average cart size last quarter?',
    '6': 'Show me last year revenue by branch',
    '7': 'What is the top branch by revenue?',
    '8': 'What is the total revenue this year comparing with the previous one?',
    '9': 'What is the monthly revenue trend this year?',
    '10': 'Compare revenue trend for two top branches',
    '11': 'Which of my branches made the most revenue last quarter?',
    '12': 'Show me revenue by branch for last two years'
  },
  de: {
    '1': 'Kann ich die Liste meiner Filialen sehen?',
    '2': 'Wie hoch war der Umsatz jeder Filiale im letzten Monat?',
    '3': 'Wie hoch war der Gesamtumsatz in diesem Jahr?',
    '4': 'Wie hoch war der durchschnittliche Umsatz in diesem Jahr?',
    '5': 'Wie hoch war der durchschnittliche Warenkorbwert im letzten Quartal?',
    '6': 'Zeig mir den Umsatz des letzten Jahres nach Filiale',
    '7': 'Welche Filiale hat den höchsten Umsatz?',
    '8': 'Wie hoch ist der Gesamtumsatz in diesem Jahr im Vergleich zum Vorjahr?',
    '9': 'Wie entwickelt sich der monatliche Umsatz in diesem Jahr?',
    '10': 'Vergleiche die Umsatzentwicklung meiner zwei umsatzstärksten Filialen',
    '11': 'Welche meiner Filialen hat im letzten Quartal den meisten Umsatz gemacht?',
    '12': 'Zeig mir den Umsatz nach Filiale für die letzten zwei Jahre'
  }
}

/* which prompt ids appear as start-screen chips and in each topic group */
const PROMPT_IDS = {
  top: ['1', '2', '3', '4', '5'],
  sales: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
  branches: ['11', '12']
}

/* 'de', 'de-DE' and 'de-CH' map to German; any other locale falls back to English */
const toLanguage = (locale: string): PromptLanguage =>
  locale.slice(0, 2).toLowerCase() === 'de' ? 'de' : 'en'

/*
 * the suggested questions: chips on the start screen and the full list by topic; call from a
 * component's setup. Without a locale argument the dashboard's current i18n locale is used.
 */
export default {
  setup(locale?: string): { topList: PromptItem[]; promptList: PromptGroup[] } {
    const { t, locale: i18nLocale } = useI18n()
    const texts = PROMPT_TEXTS[toLanguage(locale ?? i18nLocale.value ?? 'en')]
    const toItems = (ids: string[]): PromptItem[] =>
      ids.map((id) => ({ id, text: texts[id] ?? '' }))

    const topList: PromptItem[] = toItems(PROMPT_IDS.top)

    const promptList: PromptGroup[] = [
      {
        groupId: 'sales',
        label: t('aichat.main.prompts.sales'),
        icon: 'chart',
        items: toItems(PROMPT_IDS.sales)
      },
      {
        groupId: 'branches',
        icon: 'building',
        label: t('aichat.main.prompts.branches'),
        items: toItems(PROMPT_IDS.branches)
      }
      // TODO: update prompt list on mcp tools availability
      // {
      //   groupId: 'stock',
      //   icon: 'box',
      //   label: t('aichat.main.prompts.stock'),
      //   items: [
      //     { id: '', text: 'Show me items running low on stock' },
      //     { id: '', text: 'What has not sold in 30 days?' },
      //     { id: '', text: 'Which supplier delivers oat milk?' },
      //     { id: '', text: 'Do I have enough stock for the weekend?' }
      //   ]
      // },
      // {
      //   groupId: 'staff',
      //   icon: 'staff',
      //   label: t('aichat.main.prompts.staff'),
      //   items: [
      //     { id: '', text: 'Who is clocked in right now?' },
      //     { id: '', text: 'Any tables that have been seated a long time?' },
      //     { id: '', text: 'Show me the shift plan for tomorrow' },
      //     { id: '', text: 'Who worked the most hours this month?' }
      //   ]
      // },
      // {
      //   groupId: 'vouchers',
      //   icon: 'calendar',
      //   label: t('aichat.main.prompts.vouchers'),
      //   items: [
      //     { id: '', text: 'How many vouchers did customers redeem this month?' },
      //     { id: '', text: 'How many reservations do I have today?' },
      //     { id: '', text: 'Which staff member had the most bookings last month?' },
      //     { id: '', text: 'How do I cancel a reservation?' }
      //   ]
      // },
      // {
      //   groupId: 'settings',
      //   icon: 'settings',
      //   label: t('aichat.main.prompts.settings'),
      //   items: [
      //     { id: '', text: 'How do I change my opening hours?' },
      //     { id: '', text: 'How do I add a new employee?' },
      //     { id: '', text: 'How do I set up a discount?' },
      //     { id: '', text: 'How do I add a new product?' },
      //     { id: '', text: 'How do I set up a holiday closure?' }
      //   ]
      // },
      // {
      //   groupId: 'suggestions',
      //   icon: 'idea',
      //   label: t('aichat.main.prompts.suggestions'),
      //   items: [
      //     { id: '', text: 'What is one change that would improve my business?' },
      //     { id: '', text: 'Give me tips to grow my online orders' }
      //   ]
      // }
    ]

    return {
      topList,
      promptList
    }
  }
}
